import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Trophy,
  Filter,
  RefreshCw,
  Users,
  TrendingUp,
  AlertTriangle,
  IndianRupee,
  Medal,
} from "lucide-react";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

import { motion } from "framer-motion";

import api from "../../common/apiClient";
import SummaryApi from "../../common/SummaryApi";
import MISFilterDrawer from "../MISFilterDrawer";


const TopSalesPerformersSkeleton = () => {
  return (
    <div className="bg-white rounded-2xl border p-5 animate-pulse">
      <div className="h-6 w-52 bg-gray-200 rounded mb-6" />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="h-24 bg-gray-100 rounded-xl" />
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="h-[350px] bg-gray-100 rounded-xl" />

        <div className="h-[350px] bg-gray-100 rounded-xl" />
      </div>

      <div className="mt-6 h-[250px] bg-gray-100 rounded-xl" />
    </div>
  );
};

const TopSalesPerformersError = ({ onRetry }) => {
  return (
    <div className="bg-white rounded-2xl border border-red-200 p-6 text-center">
      <h3 className="text-lg font-semibold text-red-600">
        Failed to load Top Sales Performers
      </h3>

      <button
        onClick={onRetry}
        className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg"
      >
        Retry
      </button>
    </div>
  );
};

const TopSalesPerformersCard = () => {
  const [loading, setLoading] = useState(true);

  const [error, setError] = useState(false);

  const [data, setData] = useState(null);

  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    filterType: "thisMonth",
    divisionId: "",
    userId: "",
    stageId: "",
    industryType: "",
    fromDate: "",
    toDate: "",
  });

  const [appliedFilters, setAppliedFilters] = useState({
    filterType: "thisMonth",
    divisionId: "",
    userId: "",
    stageId: "",
    industryType: "",
    fromDate: "",
    toDate: "",
  });

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);

      const params = new URLSearchParams();

      Object.entries(appliedFilters).forEach(([key, value]) => {
        if (value !== "" && value !== null && value !== undefined) {
          params.append(key, value);
        }
      });

      const response = await api.get(
        `${SummaryApi.getTopSalesPerformers.url}?${params.toString()}`,
      );

      if (response?.data?.success) {
        setData(response.data.data);
      }
    } catch (err) {
      console.error(err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const topPerformer = useMemo(() => {
    if (!data?.performers?.length) return null;

    return [...data.performers].sort(
      (a, b) => b.forecastRevenue - a.forecastRevenue,
    )[0];
  }, [data]);

  if (loading) {
    return <TopSalesPerformersSkeleton />;
  }

  if (error) {
    return <TopSalesPerformersError onRetry={fetchReport} />;
  }

  const totalForecastRevenue = data.performers.reduce(
    (sum, item) => sum + item.forecastRevenue,
    0,
  );

  const totalPipeline = data.performers.reduce(
    (sum, item) => sum + item.pipelineValue,
    0,
  );

  const totalOverdue = data.performers.reduce(
    (sum, item) => sum + item.overdueFollowups,
    0,
  );

  const stats = [
    {
      title: "Sales Team",
      value: data.summary.totalUsers,
      icon: Users,
      bg: "bg-blue-50",
      color: "text-blue-600",
    },
    {
      title: "Pipeline Value",
      value: `₹${totalPipeline.toLocaleString("en-IN")}`,
      icon: IndianRupee,
      bg: "bg-green-50",
      color: "text-green-600",
    },
    {
      title: "Forecast Revenue",
      value: `₹${totalForecastRevenue.toLocaleString("en-IN")}`,
      icon: TrendingUp,
      bg: "bg-red-50",
      color: "text-red-600",
    },
    {
      title: "Overdue Followups",
      value: totalOverdue,
      icon: AlertTriangle,
      bg: "bg-orange-50",
      color: "text-orange-600",
    },
  ];

  return (
    <>
      <motion.div
        initial={{
          opacity: 0,
          y: 20,
        }}
        whileInView={{
          opacity: 1,
          y: 0,
        }}
        transition={{
          duration: 0.4,
        }}
        viewport={{
          once: true,
        }}
        className="bg-white rounded-2xl border border-gray-200 shadow-sm"
      >
        <div className="flex items-center justify-between p-5 border-b">
          <div className="flex items-center gap-2">
            <Trophy size={18} className="text-red-600" />

            <h2 className="font-semibold text-lg">Top Sales Performers</h2>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={fetchReport}
              className="h-9 w-9 rounded-lg border flex items-center justify-center"
            >
              <RefreshCw size={15} />
            </button>

            <button
              onClick={() => setShowFilters(true)}
              className="h-9 w-9 rounded-lg bg-red-600 text-white flex items-center justify-center"
            >
              <Filter size={15} />
            </button>
          </div>
        </div>

        <div className="p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
            {stats.map((item) => {
              const Icon = item.icon;

              return (
                <div key={item.title} className={`${item.bg} rounded-xl p-4`}>
                  <div className="flex justify-between">
                    <div>
                      <p className="text-xs text-gray-500">{item.title}</p>

                      <h3 className={`mt-2 text-xl font-bold ${item.color}`}>
                        {item.value}
                      </h3>
                    </div>

                    <Icon size={20} className={item.color} />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {/* Revenue Chart */}

            <div className="rounded-xl border p-4 h-[350px]">
              <h3 className="font-semibold mb-4">Forecast Revenue Ranking</h3>

              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.chart} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />

                  <XAxis type="number" />

                  <YAxis dataKey="userName" type="category" width={100} />

                  <Tooltip />

                  <Bar
                    dataKey="forecastRevenue"
                    fill="#DC2626"
                    radius={[0, 6, 6, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Top Performer */}

            <div className="rounded-xl border p-5">
              <div className="flex items-center gap-2 mb-4">
                <Medal size={20} className="text-yellow-500" />

                <h3 className="font-semibold">Best Performer</h3>
              </div>

              {topPerformer && (
                <>
                  <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-5 border">
                    <h4 className="text-xl font-bold text-gray-900">
                      {topPerformer.userName}
                    </h4>

                    <p className="text-sm text-gray-500 mt-1">
                      Leading Revenue Forecast
                    </p>

                    <div className="grid grid-cols-2 gap-4 mt-5">
                      <div>
                        <p className="text-xs text-gray-500">Leads</p>

                        <p className="font-bold text-lg">
                          {topPerformer.leadCount}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs text-gray-500">Pipeline</p>

                        <p className="font-bold text-lg">
                          ₹{topPerformer.pipelineValue.toLocaleString("en-IN")}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs text-gray-500">Forecast</p>

                        <p className="font-bold text-lg text-green-600">
                          ₹
                          {topPerformer.forecastRevenue.toLocaleString("en-IN")}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs text-gray-500">Overdue</p>

                        <p className="font-bold text-lg text-red-600">
                          {topPerformer.overdueFollowups}
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Leaderboard Table */}

          <div className="mt-6 overflow-x-auto border rounded-xl">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left">Rank</th>

                  <th className="px-4 py-3 text-left">Sales Person</th>

                  <th className="px-4 py-3 text-left">Leads</th>

                  <th className="px-4 py-3 text-left">Pipeline</th>

                  <th className="px-4 py-3 text-left">Forecast</th>

                  <th className="px-4 py-3 text-left">Overdue</th>
                </tr>
              </thead>

              <tbody>
                {[...data.performers]
                  .sort((a, b) => b.forecastRevenue - a.forecastRevenue)
                  .map((item, index) => (
                    <tr key={item.userId} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <span
                          className={`h-8 w-8 rounded-full flex items-center justify-center text-white text-sm font-semibold ${
                            index === 0
                              ? "bg-yellow-500"
                              : index === 1
                                ? "bg-gray-400"
                                : index === 2
                                  ? "bg-orange-500"
                                  : "bg-slate-500"
                          }`}
                        >
                          {index + 1}
                        </span>
                      </td>

                      <td className="px-4 py-3 font-medium">{item.userName}</td>

                      <td className="px-4 py-3">{item.leadCount}</td>

                      <td className="px-4 py-3">
                        ₹{item.pipelineValue.toLocaleString("en-IN")}
                      </td>

                      <td className="px-4 py-3 text-green-600 font-medium">
                        ₹{item.forecastRevenue.toLocaleString("en-IN")}
                      </td>

                      <td className="px-4 py-3 text-red-600">
                        {item.overdueFollowups}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {/* Insight */}

          {topPerformer && (
            <div className="mt-6 rounded-xl bg-red-50 border border-red-200 p-4">
              <h4 className="font-semibold text-red-700 mb-2">
                Performance Insight
              </h4>

              <p className="text-sm text-gray-700">
                <strong>{topPerformer.userName}</strong> currently leads the
                sales team with{" "}
                <strong>
                  ₹{topPerformer.forecastRevenue.toLocaleString("en-IN")}
                </strong>{" "}
                forecast revenue and <strong>{topPerformer.leadCount}</strong>{" "}
                active opportunities.
              </p>
            </div>
          )}
        </div>
      </motion.div>

      <MISFilterDrawer
        open={showFilters}
        onClose={() => setShowFilters(false)}
        filters={filters}
        setFilters={setFilters}
        onApply={() => {
          setAppliedFilters(filters);

          setShowFilters(false);
        }}
      />
    </>
  );
};

export default TopSalesPerformersCard;
